import type { CareRequestModelView, CareRequestSubjectView } from '@actschurch/ccms-sdk';
import type { DateIssue } from '../dateHelpers';
import { cleanField, cleanText, PERSON_FIELDS, type Cleaned, type PersonField } from './rules';

export type Source = 2 | 3;

export interface PersonResult {
  values: Record<PersonField, string | null>;
  /** Where each filled value came from (2 = request detail, 3 = member record). */
  from: Partial<Record<PersonField, Source>>;
  /** Why a date of birth is empty, when it was present but unusable. */
  issues: Partial<Record<PersonField, DateIssue>>;
  /** The value exactly as received when it was present but unusable (e.g. a bad date), so a person can see it. */
  rawIssues?: Partial<Record<PersonField, string>>;
  /** Used for the safe Source 3 route (getViewById). */
  memberID: number | null;
}

export interface RecordResult {
  /** requestID or intakeID, depending on the source. */
  id: number;
  reference: string;
  location: string | null;
  a: PersonResult;
  b: PersonResult | null;
  /** People beyond the second one. Counted in the summary, never exported. */
  extraPeople: number;
  /** The detail call failed. */
  failed: boolean;
  /** Whether an empty location counts as missing (true for requests, false for intakes). */
  requireLocation: boolean;
}

export interface MissingItem {
  field: PersonField | 'location';
  issue: DateIssue | null;
}

export const emptyPerson = (memberID: number | null = null): PersonResult => ({
  values: { firstName: null, surname: null, email: null, phone: null, gender: null, dateOfBirth: null },
  from: {},
  issues: {},
  rawIssues: {},
  memberID,
});

export const failedResult = (
  id: number, reference: string, location: string | null, requireLocation: boolean,
): RecordResult => ({
  id, reference, location, a: emptyPerson(), b: null, extraPeople: 0, failed: true, requireLocation,
});

export const missingOf = (p: PersonResult): MissingItem[] =>
  PERSON_FIELDS.filter((f) => p.values[f] === null).map((f) => ({ field: f, issue: p.issues[f] ?? null }));

// ── Candidate lists: the places to try, in order, for each field ─────────────────────────────────

export type Candidates = Record<PersonField, unknown[]>;

/** First candidate that passes the missing rule wins. Remembers a date problem for the report. */
export function pickFirst(field: PersonField, candidates: unknown[]): Cleaned & { raw?: string } {
  let issue: DateIssue | null = null;
  let rawIssue: string | undefined;
  for (const raw of candidates) {
    const c = cleanField(field, raw);
    if (c.value !== null) return c;
    if (c.issue && !issue) { issue = c.issue; rawIssue = String(raw).trim(); }
  }
  return { value: null, issue, raw: rawIssue };
}

export function buildPerson(c: Candidates, memberID: number | null): PersonResult {
  const p = emptyPerson(memberID);
  for (const f of PERSON_FIELDS) {
    const got = pickFirst(f, c[f]);
    p.values[f] = got.value;
    if (got.value !== null) p.from[f] = 2;
    else if (got.issue) {
      p.issues[f] = got.issue;
      if (got.raw) (p.rawIssues ??= {})[f] = got.raw;
    }
  }
  return p;
}

const ownOf = (s: CareRequestSubjectView): Candidates => ({
  firstName: [s.firstName], surname: [s.surname], email: [s.email],
  phone: [s.phone], gender: [s.gender], dateOfBirth: [s.dateOfBirth],
});

const requesterOf = (d: CareRequestModelView): Candidates => ({
  firstName: [d.firstName], surname: [d.surname], email: [d.email],
  phone: [d.phone], gender: [d.gender], dateOfBirth: [],
});

const onBehalfOf = (d: CareRequestModelView): Candidates => ({
  firstName: [d.subjectFirstName], surname: [d.subjectSurname], email: [d.subjectEmail],
  phone: [d.subjectPhone], gender: [d.subjectGender], dateOfBirth: [],
});

const norm = (v: unknown) => String(v ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
export const sameText = (a: unknown, b: unknown) => norm(a) !== '' && norm(a) === norm(b);

/** The custom answer `date_of_birth` — the date of birth of the person the request applies to. */
export function customDob(d: CareRequestModelView): string | null {
  const hit = (d.customData ?? []).find(
    (cd) => (cd.fieldKey ?? '').toLowerCase().replace(/[\s-]/g, '_') === 'date_of_birth',
  );
  return hit?.value?.trim() || null;
}

/**
 * Plan section 4 + 6: who needs counselling, and where each of their values lives.
 *
 *  A. `subjects[]` not empty → A = the entry marked main (else the first), B = the other entry.
 *  B. empty + on behalf      → A = the on-behalf block; the requester is only the contact.
 *  C. empty + for self       → A = the requester.
 *
 * Person B only ever reads their own entry. Person A may fall back to the requester (self requests)
 * or the on-behalf block, but with two people that fallback is only trusted when its first name matches
 * Person A's, so it can never leak the other person's details into A.
 */
export function resolveRequest(
  d: CareRequestModelView,
  ctx: { id: number; reference: string; listLocation: string | null },
): RecordResult {
  const subs = d.subjects ?? [];
  const forSelf = d.isForSelf !== false; // only an explicit false means "on behalf"
  const location = cleanText(ctx.listLocation) ?? cleanText(d.locationName);
  const fallback = forSelf ? requesterOf(d) : onBehalfOf(d);

  let a: PersonResult;
  let b: PersonResult | null = null;
  let extraPeople = 0;

  if (subs.length > 0) {
    const main = subs.find((s) => s.isMain) ?? subs[0];
    const other = subs.find((s) => s !== main) ?? null;
    extraPeople = Math.max(0, subs.length - 2);

    const fallbackApplies = subs.length === 1 || sameText(main.firstName, fallback.firstName[0]);
    const cand = ownOf(main);
    if (fallbackApplies) {
      for (const f of PERSON_FIELDS) if (f !== 'dateOfBirth') cand[f] = [...cand[f], ...fallback[f]];
    }
    // The custom answer is one person's date of birth, so with two people it is ambiguous.
    cand.dateOfBirth = [main.dateOfBirth, ...(subs.length === 1 ? [customDob(d)] : [])];

    a = buildPerson(cand, main.memberID ?? (forSelf && fallbackApplies ? d.matchedMemberID ?? null : null));
    if (other) b = buildPerson(ownOf(other), other.memberID ?? null);
  } else {
    a = buildPerson({ ...fallback, dateOfBirth: [customDob(d)] }, forSelf ? d.matchedMemberID ?? null : null);
  }

  return {
    id: ctx.id, reference: ctx.reference, location, a, b, extraPeople, failed: false, requireLocation: true,
  };
}
